import {
    Dialog,
    DialogContent, 
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'  
import { useOffline } from '@/context/OfflineContext' 
import CardProduct from './components/CardProduct';
import { useToast } from '@/components/ui/use-toast'
import { useMutation, useQueryClient } from '@tanstack/react-query' 
import { createResearchedProduct } from '@/api/business/marketResearch'
import { AnimatePresence } from 'framer-motion';
import TopMessage from '@/components/TopMessage';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { ResearchedProductKeys } from '@/queries/useResearchedProductQuery';

interface AddDialogProps {
    isOpen: boolean
    onClose: () => void
}

interface productsOffline {
    id: number;
    DS_REVENDA: string;
    SK_PESQUISA: number;
    SK_CLIENTE: number;
    DS_NOME_FANTASIA: string | null;
    SK_PRODUTO: string;
    DS_PRODUTO: string;
    VL_PRECO_COMPRA: number;
    QTD_VOLUME_COMPRA: number;
    VL_PRECO_VENDA: number;
    DD_LATITUDE: string;
    DD_LONGITUDE: string;
    DD_PRODUTO_EXTRA: number;
    IMAGEM: File | null;
    CREATED_AT: string;
}

const ProductsOfflineDialog = ({ isOpen, onClose }: AddDialogProps) => {  
    const queryClient = useQueryClient() 
    const online = useOnlineStatus();
    const { data, removeItem } = useOffline();
    const { toast } = useToast() 
 
    
    const { mutate, isPending } = useMutation({
        mutationFn: createResearchedProduct,
        onSuccess: (_, variables) => { 
            queryClient.invalidateQueries({ queryKey: ResearchedProductKeys.details() })
            removeItem('ProductsFromCustomer', variables.id) 

            setTimeout(() => {
                if(!data.ProductsFromCustomer) {
                    onClose()
                }
            }, 2000)
            
            toast({
                title: 'Produto adicionado na pesquisa'
            }) 
        },
        onError: () => {
            let errorMessage = 'Não foi possível adicionar o produto. Tente novamente mais tarde.'

            toast({
                title: 'Erro ao adicionar o produto',
                description: errorMessage,
                variant: 'destructive',
            })
        },
    })

    const handleSaveItem = (data: productsOffline) => {
        if (!online) {
            toast({
                title: 'Sem conexão com a rede',
                description: "Tente novamente quanto a rede voltar!",
                variant: 'warning',
            })
            return
        }
        mutate({
            id: data.id,
            SK_PESQUISA: data.SK_PESQUISA, 
            SK_CLIENTE: data.SK_CLIENTE, 
            SK_PRODUTO: data.SK_PRODUTO, 
            VL_PRECO_COMPRA: data.VL_PRECO_COMPRA,
            QTD_VOLUME_COMPRA: data.QTD_VOLUME_COMPRA,
            VL_PRECO_VENDA: data.VL_PRECO_VENDA,
            DD_LATITUDE: data.DD_LATITUDE,
            DD_LONGITUDE: data.DD_LONGITUDE,  
            IMAGEM: data.IMAGEM,
            CREATED_AT: data.CREATED_AT,
            DD_PRODUTO_EXTRA: data.DD_PRODUTO_EXTRA
        })

    }

    const handleDeleteItem = ( id: number ) => {
        removeItem('ProductsFromCustomer', id)

        toast({
            title: 'Produto removido!', 
            variant: 'success',
        })
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className='max-w-[600px]  overflow-auto' aria-describedby='productsOffline'>
                <DialogHeader className='px-2'>
                    <DialogTitle>Produtos aguardando ações</DialogTitle>
                </DialogHeader>
                <AnimatePresence> 
                    {isPending && (
                        <TopMessage
                            text="Salvando produto..."
                            variant="loading"
                            className="text-green-500 bg-green-100"
                        />
                    )} 
                </AnimatePresence>
                <div
                    className={`fixed top-3 right-10 z-50 px-3 py-1 rounded-full text-xs font-medium
                        ${online ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}
                    `}
                    >
                    {online ? "Online" : "Offline"}
                </div>
                <div className='max-h-[500px]'>
                    <div className='grid gap-4 md:grid-cols-2 grid-cols-1'>
                        {data.ProductsFromCustomer?.map((item: productsOffline, index) => (
                            <CardProduct 
                                key={index} 
                                productsOffline={item} 
                                saveItem={() => handleSaveItem(item)} 
                                deleteItem={() => handleDeleteItem(item.id)}
                            /> 
                        ))}
                    </div>

                </div>
                
            </DialogContent>
        </Dialog>
    )
}

export default ProductsOfflineDialog
